import React, { useMemo } from 'react';

const ASSET_CLASS_LABELS = {
  commercial_property: 'Commercial Property',
  warehouse: 'Warehouse',
  factory_industrial: 'Factory / Industrial Land',
  industrial_shed: 'Industrial Shed',
  machinery: 'Machinery',
  equipment_infra: 'Equipment & Infrastructure',
};

const TRANSACTION_LABELS = {
  lease: 'Lease',
  rent: 'Rent',
  sale: 'Sale',
  lease_to_own: 'Lease to Own',
};

const RENTAL_RATE_LABELS = {
  total_purchase: 'Total purchase',
  per_month: 'Per month (lease/rent)',
  per_day: 'Per day',
  per_hour: 'Per hour',
};

function str(v) {
  if (v == null || v === '') return '';
  return String(v).trim();
}

function formatIso(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export function statusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'draft') return 'text-bg-warning text-dark';
  if (s === 'submitted') return 'text-bg-success';
  return 'text-bg-secondary';
}

function Field({ label, children }) {
  if (children == null || children === '' || children === '—') {
    return null;
  }
  return (
    <>
      <dt className="col-sm-3">{label}</dt>
      <dd className="col-sm-9">{children}</dd>
    </>
  );
}

/**
 * Read-only buyer requirement cards (shared by buyer dashboard detail and seller enquiry view).
 * @param {{ detail: { status: string, updatedAt: string, form: object, locationLabel?: string, countryName?: string|null, stateName?: string|null, cityName?: string|null } }} props
 */
export default function BuyerRequirementDetailView({ detail }) {
  const f = detail?.form && typeof detail.form === 'object' ? detail.form : {};

  const summary = useMemo(() => {
    const assetClass = str(f.assetClass) || 'commercial_property';
    return {
      assetLabel: ASSET_CLASS_LABELS[assetClass] || assetClass,
      transaction: TRANSACTION_LABELS[str(f.transactionType)] || str(f.transactionType) || '—',
      budget: `₹${str(f.budgetMin) || '—'} – ₹${str(f.budgetMax) || '—'}`,
      rateType: RENTAL_RATE_LABELS[str(f.rentalRateType)] || str(f.rentalRateType) || '—',
    };
  }, [f]);

  if (!detail) {
    return null;
  }

  const isDraft = String(detail.status).toLowerCase() === 'draft';

  return (
    <>
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <div>
          <h1 className="h4 mb-1">Requirement detail</h1>
          <p className="text-muted small mb-0">Updated {formatIso(detail.updatedAt)}</p>
        </div>
        <span className={`badge ${statusBadgeClass(detail.status)} text-uppercase`}>{detail.status}</span>
      </div>

      {isDraft ? (
        <div className="alert alert-info py-2 small mb-3">
          This requirement is still a draft. If you are the buyer, you can continue editing from Post your requirement.
        </div>
      ) : null}

      <div className="card ile-buyer-req-card shadow-sm mb-3">
        <div className="card-body">
          <h2 className="h6 text-muted text-uppercase small mb-3">Summary</h2>
          <dl className="row mb-0 small">
            <Field label="Purpose">{str(f.businessPurpose)}</Field>
            <Field label="Asset type">{summary.assetLabel}</Field>
            <Field label="Listing type">
              {(() => {
                const lt = str(f.listingType).toLowerCase();
                if (lt === 'machinery') return 'Machinery';
                if (lt === 'property') return 'Property';
                return null;
              })()}
            </Field>
            <Field label="Transaction">{summary.transaction}</Field>
            <Field label="Location">{detail.locationLabel || str(f.locationDetails) || '—'}</Field>
            <Field label="Budget">{summary.budget}</Field>
            <Field label="Budget type">{summary.rateType}</Field>
          </dl>
        </div>
      </div>

      <div className="card ile-buyer-req-card shadow-sm mb-3">
        <div className="card-body">
          <h2 className="h6 text-muted text-uppercase small mb-3">Requirement</h2>
          <dl className="row mb-0 small">
            <Field label="Description">
              {str(f.description) ? <span className="text-break">{str(f.description)}</span> : null}
            </Field>
            <Field label="Industry / business">{str(f.industryBusinessType)}</Field>
            <Field label="Required by">{str(f.requiredBy)}</Field>
            <Field label="Urgency">{str(f.urgency)}</Field>
            <Field label="Duration">{`${str(f.minDuration) || '—'} – ${str(f.maxDuration) || '—'} ${str(f.durationType) || ''}`}</Field>
            <Field label="Country">{detail.countryName}</Field>
            <Field label="State">{detail.stateName}</Field>
            <Field label="City">{detail.cityName}</Field>
            <Field label="Additional location notes">{str(f.locationDetails)}</Field>
            <Field label="Territory">{str(f.territoryFlexible) === 'any' ? 'Flexible (any)' : 'Strict'}</Field>
            <Field label="Budget negotiable">{f.budgetNegotiable ? 'Yes' : 'No'}</Field>
            <Field label="Project-based duration">{f.projectBased ? 'Yes' : 'No'}</Field>
          </dl>
        </div>
      </div>

      <div className="card ile-buyer-req-card shadow-sm mb-3">
        <div className="card-body">
          <h2 className="h6 text-muted text-uppercase small mb-3">Contact</h2>
          <dl className="row mb-0 small">
            <Field label="Name">{str(f.contactName)}</Field>
            <Field label="Company">{str(f.company)}</Field>
            <Field label="Email">{str(f.email)}</Field>
            <Field label="Phone">{str(f.phone)}</Field>
          </dl>
        </div>
      </div>
    </>
  );
}
